import React from 'react';
import { FiLogOut, FiX } from 'react-icons/fi';

import { Button } from '@design-system/components/atoms/Button';

export interface LogoutModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when modal is closed
   */
  onClose: () => void;

  /**
   * Callback when logout is confirmed
   */
  onConfirm: () => void;

  /**
   * Whether logout is in progress
   */
  isLoading?: boolean;
}

/**
 * LogoutModal component
 * Modal for confirming user logout action
 */
export const LogoutModal: React.FC<LogoutModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-modal-title"
      tabIndex={-1}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-neutral-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
              <FiLogOut className="w-6 h-6 text-purple-600" />
            </div>
            <h2 id="logout-modal-title" className="text-xl font-semibold text-neutral-900">
              Sair do sistema
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-50"
            aria-label="Fechar"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-neutral-600 text-center">Tem certeza que deseja sair do sistema?</p>
          <p className="text-neutral-500 text-sm text-center mt-2">
            Você precisará fazer login novamente para acessar o painel.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-200 bg-neutral-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={isLoading}>
            {isLoading ? 'Saindo...' : 'Sim, sair'}
          </Button>
        </div>
      </div>
    </div>
  );
};
